import type { ReactNode } from "react";
import { useState } from "react";
import { Box, Button, Link as MuiLink, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import IpText from "./IpText.tsx";
import { DataTable } from "@/components/DesignSystem";
import { attemptsText, NO_OTHER_SOURCES, NO_SIGN_INS } from "@/utils/alertCopy.ts";
import { countryName } from "@/utils/countryName.ts";
import { formatAsn } from "@/utils/formatAsn.ts";
import { formatDateTime } from "@/utils/formatDateTime.ts";
import { ipActivityIpLink } from "@/utils/ipActivityLink.ts";
import type { AccountSource, AlertEvidenceSource } from "@/types";

const TOP_N = 10;
const TRAIL_DAYS = 30;

function IpLink({ ip }: { readonly ip: string }) {
  return (
    <MuiLink component={RouterLink} to={ipActivityIpLink(TRAIL_DAYS, ip)}>
      <IpText ip={ip} />
    </MuiLink>
  );
}

function LastSeen({ source }: { readonly source: AlertEvidenceSource }) {
  return (
    <Tooltip title={`First seen ${formatDateTime(source.first_seen)}`}>
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {formatDateTime(source.last_seen)}
      </Box>
    </Tooltip>
  );
}

const place = (s: AlertEvidenceSource) => (s.country ? countryName(s.country) : "—");
const network = (s: AlertEvidenceSource) => formatAsn(s.asn, s.as_name) ?? "—";
const byLastSeen = <T extends { last_seen: number }>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => b.last_seen - a.last_seen);

const SIGN_IN_COLUMNS = [
  { header: "IP", accessor: (s: AccountSource) => <IpLink ip={s.ip} /> },
  { header: "Country", accessor: (s: AccountSource) => place(s) },
  { header: "Network", accessor: (s: AccountSource) => network(s) },
  {
    header: "Sign-ins",
    accessor: (s: AccountSource) => (
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {s.sign_ins.toLocaleString("en-US")}
      </Box>
    ),
    align: "right" as const,
  },
  { header: "Last seen", accessor: (s: AccountSource) => <LastSeen source={s} /> },
];

const ATTACK_COLUMNS = [
  { header: "IP", accessor: (s: AlertEvidenceSource) => <IpLink ip={s.ip} /> },
  { header: "Country", accessor: (s: AlertEvidenceSource) => place(s) },
  { header: "Network", accessor: (s: AlertEvidenceSource) => network(s) },
  { header: "Attempts", accessor: (s: AlertEvidenceSource) => attemptsText(s.attempts) },
  { header: "Last seen", accessor: (s: AlertEvidenceSource) => <LastSeen source={s} /> },
];

interface SourceColumn<T> {
  readonly header: string;
  readonly accessor: keyof T | ((row: T) => ReactNode);
  readonly align?: "left" | "right" | "center";
}

/** A source table capped at the top 10 (already sorted newest first), with its own "show all" toggle. */
function CappedSourceTable<T extends AlertEvidenceSource>({
  columns,
  rows,
}: {
  readonly columns: readonly SourceColumn<T>[];
  readonly rows: readonly T[];
}) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? rows : rows.slice(0, TOP_N);
  return (
    <>
      <DataTable columns={columns} rows={shown} getRowKey={(s) => s.ip} />
      {rows.length > TOP_N && (
        <Button size="small" onClick={() => setShowAll((value) => !value)} sx={{ mt: 1 }}>
          {showAll ? `Show top ${TOP_N}` : `Show all ${rows.length}`}
        </Button>
      )}
    </>
  );
}

/** Sources that tried the account without signing in with it: the latest 10, then all on request. */
export function AttackingSourcesTable({ sources }: { readonly sources: readonly AlertEvidenceSource[] }) {
  if (sources.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {NO_OTHER_SOURCES}
      </Typography>
    );
  }
  return <CappedSourceTable columns={ATTACK_COLUMNS} rows={byLastSeen(sources)} />;
}

export default function AccountSources({ sources }: { readonly sources: readonly AccountSource[] }) {
  const signIns = byLastSeen(sources.filter((s) => s.sign_ins > 0));
  const others = sources.filter((s) => s.sign_ins === 0);
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Signs in from
      </Typography>
      {signIns.length > 0 ? (
        <CappedSourceTable columns={SIGN_IN_COLUMNS} rows={signIns} />
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {NO_SIGN_INS}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
        Attempts from other sources
      </Typography>
      <AttackingSourcesTable sources={others} />
    </>
  );
}
